import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye, Braces, Boxes, BrainCircuit, Lightbulb, Landmark, Route, GraduationCap, Database,
  MessagesSquare, Presentation, ShieldCheck, Radar, Loader2, Send, Sparkles, Check, X,
  ChevronDown, Scale, FlaskConical, ArrowDown, CircleCheck, CircleSlash, Clock,
} from "lucide-react";
import { Panel, Pill } from "./ui";
import { nexsysApi } from "./api";

const ICONS = {
  Eye, Braces, Boxes, BrainCircuit, Lightbulb, Landmark, Route, GraduationCap, Database,
  MessagesSquare, Presentation, ShieldCheck, Radar,
};
const Icon = ({ name, ...p }) => { const C = ICONS[name] || Sparkles; return <C {...p} />; };

const SUGGESTIONS = [
  "Should we accelerate ERP integration testing given the vendor API risk?",
  "Lock cloud egress commitments this quarter — yes or no?",
  "How should we resource the Enterprise Data Fabric next sprint?",
  "Greenlight Data Center Phase 2 procurement now?",
];

/* ============ SCIOUS → NEXSYS → EDIOS stack ============ */
function StackLayer({ node, tone, index }) {
  const map = {
    muted: "border-white/10 bg-white/[0.03] text-white/70",
    core: "border-cyan-400/40 bg-gradient-to-r from-cyan-500/15 to-indigo-500/15 text-white shadow-[0_0_40px_rgba(34,211,238,0.15)]",
    edios: "border-indigo-400/30 bg-indigo-500/[0.08] text-white/80",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.12, duration: 0.5 }}
      className={`relative w-full rounded-2xl border px-5 py-4 ${map[tone]}`}
    >
      <div className="flex items-center gap-3">
        <span className={`font-display text-lg font-bold tracking-tight ${tone === "core" ? "text-cyan-300" : ""}`}>{node.name}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{node.tagline}</span>
      </div>
      <p className="mt-1.5 text-[12px] leading-relaxed text-white/50">{node.desc}</p>
    </motion.div>
  );
}

function StackVisual({ stack }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <StackLayer node={stack.above} tone="muted" index={0} />
      <ArrowDown className="h-4 w-4 text-cyan-400/50" />
      <StackLayer node={stack.core} tone="core" index={1} />
      <ArrowDown className="h-4 w-4 text-indigo-400/50" />
      <StackLayer node={stack.below} tone="edios" index={2} />
    </div>
  );
}

/* ============ Agent pipeline (activation + findings) ============ */
function AgentRow({ a, state, index, open, onToggle }) {
  // state: "pending" | "active" | "done"
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: index * 0.02 }}
      className={`rounded-xl border transition-colors ${state === "pending" ? "border-white/[0.05] bg-white/[0.01] opacity-45" : "border-white/10 bg-white/[0.03]"}`}
      data-testid={`agent-row-${a.key}`}
    >
      <button onClick={onToggle} disabled={!a.finding || a.finding === "—"} className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `${a.accent}1f`, color: a.accent }}>
          <Icon name={a.icon} className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-display text-[13px] font-semibold text-white/90">{a.agent}</span>
            <span className="truncate font-mono text-[10px] text-white/35">{a.question}</span>
          </div>
          {state === "done" && a.finding !== "—" && (
            <p className={`mt-0.5 text-[12px] leading-snug text-white/60 ${open ? "" : "line-clamp-1"}`}>{a.finding}</p>
          )}
          {state === "active" && <p className="mt-0.5 text-[12px] text-cyan-300/80">reasoning…</p>}
        </div>
        {state === "active" && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-cyan-300" />}
        {state === "done" && (
          <div className="flex shrink-0 items-center gap-2">
            <span className="font-mono text-[11px]" style={{ color: a.accent }}>{a.confidence}%</span>
            {a.finding !== "—" && <ChevronDown className={`h-4 w-4 text-white/30 transition-transform ${open ? "rotate-180" : ""}`} />}
          </div>
        )}
      </button>
    </motion.div>
  );
}

function AgentPipeline({ agents, running, activeIdx, result }) {
  const [openKey, setOpenKey] = useState(null);
  const list = result ? (result.agents || agents) : agents;
  return (
    <div className="space-y-1.5" data-testid="agent-pipeline">
      {list.map((a, i) => {
        let st = "done";
        if (running && !result) st = i < activeIdx ? "done" : i === activeIdx ? "active" : "pending";
        // during running, "done" agents have no finding yet → show as plain state
        const showAgent = result ? a : { ...a, finding: st === "done" ? "" : a.finding };
        return (
          <AgentRow
            key={a.key} a={result ? a : showAgent} index={i}
            state={result ? "done" : st}
            open={openKey === a.key}
            onToggle={() => setOpenKey(openKey === a.key ? null : a.key)}
          />
        );
      })}
    </div>
  );
}

/* ============ Governed Decision Object ============ */
function ConfidenceBar({ value, color = "#22d3ee" }) {
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
      <motion.div initial={{ width: 0 }} animate={{ width: `${value}%` }} transition={{ duration: 0.9 }}
        className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${color}, #6366f1)` }} />
    </div>
  );
}

function Section({ label, children, icon }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{icon}{label}</div>
      {children}
    </div>
  );
}

function DecisionObject({ d, onOutcome }) {
  const [status, setStatus] = useState(d.status || "proposed");
  const [busy, setBusy] = useState("");
  const g = d.governance || {};
  const dec = d.decision || {};
  const act = async (s) => {
    setBusy(s);
    try { await nexsysApi.recordOutcome(d.id, { status: s, outcome: s }); setStatus(s); onOutcome?.(); } catch { /* ignore */ } finally { setBusy(""); }
  };
  const decided = status !== "proposed";
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="nx-panel nx-corners overflow-hidden" data-testid="decision-object">
      {/* header */}
      <div className="border-b border-white/[0.06] bg-gradient-to-r from-cyan-500/10 to-indigo-500/10 px-5 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone="cyan"><Sparkles className="h-3 w-3" /> Governed Decision Object</Pill>
          <span className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] ${g.allowed ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-red-400/30 bg-red-400/10 text-red-300"}`}>
            {g.allowed ? <CircleCheck className="h-3 w-3" /> : <CircleSlash className="h-3 w-3" />}{g.allowed ? "Governance: Allowed" : "Governance: Blocked"}
          </span>
        </div>
        <h3 className="mt-2.5 font-display text-lg font-bold leading-tight text-white">{d.title}</h3>
        <p className="mt-1 text-[13px] text-white/55">{d.summary}</p>
      </div>

      {(d.recall || []).length > 0 && (
        <div className="border-b border-white/[0.06] bg-amber-500/[0.05] px-5 py-3" data-testid="decision-recall">
          <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300/80"><Database className="h-3 w-3" /> Precedent Recall · we've decided something similar before</div>
          <div className="space-y-1">
            {d.recall.map((r, i) => {
              const c = { approved: "#34d399", rejected: "#ef4444", deferred: "#f59e0b", proposed: "#94a3b8" }[r.status] || "#94a3b8";
              return (
                <div key={i} className="flex flex-wrap items-center gap-x-2 text-[12px] text-white/70">
                  <span className="font-medium text-white/85">{r.title}</span>
                  <span className="min-w-0 truncate text-white/45">— {r.summary}</span>
                  <span className="font-mono text-[10px] uppercase" style={{ color: c }}>{r.outcome || r.status}</span>
                  <span className="font-mono text-[10px] text-white/30">{(r.created_at || "").slice(0, 10)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        {/* left: recommendation + explainability */}
        <div className="space-y-4">
          <Section label="Recommendation" icon={<Lightbulb className="h-3 w-3 text-cyan-300" />}>
            <p className="text-[14px] font-medium leading-relaxed text-white/90">{dec.recommendation}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="font-mono text-[10px] text-white/40">CONFIDENCE</span>
              <div className="flex-1"><ConfidenceBar value={dec.confidence} /></div>
              <span className="font-display text-sm font-bold text-cyan-300">{dec.confidence}%</span>
            </div>
          </Section>

          <div className="grid gap-4 sm:grid-cols-2">
            <Section label="Why" icon={<BrainCircuit className="h-3 w-3 text-cyan-300" />}>
              <p className="text-[12px] leading-relaxed text-white/60">{dec.why}</p>
            </Section>
            <Section label="How" icon={<Route className="h-3 w-3 text-cyan-300" />}>
              <p className="text-[12px] leading-relaxed text-white/60">{dec.how}</p>
            </Section>
          </div>

          <Section label="Evidence" icon={<ShieldCheck className="h-3 w-3 text-cyan-300" />}>
            <ul className="space-y-1">
              {(dec.evidence || []).map((e, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] leading-relaxed text-white/70"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />{e}</li>
              ))}
            </ul>
          </Section>

          <Section label="Alternatives Considered" icon={<Scale className="h-3 w-3 text-cyan-300" />}>
            <div className="space-y-2">
              {(dec.alternatives || []).map((a, i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-black/20 p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] font-medium text-white/85">{a.title}</span>
                    <span className="font-mono text-[10px] text-white/40">{a.confidence}%</span>
                  </div>
                  {a.tradeoff && <p className="mt-1 text-[11px] leading-snug text-white/45"><span className="text-amber-300/70">Trade-off: </span>{a.tradeoff}</p>}
                </div>
              ))}
            </div>
          </Section>

          <Section label="Execution Plan" icon={<FlaskConical className="h-3 w-3 text-cyan-300" />}>
            <ol className="space-y-1">
              {(dec.execution || []).map((e, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] leading-relaxed text-white/70">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-cyan-400/15 font-mono text-[9px] text-cyan-300">{i + 1}</span>{e}
                </li>
              ))}
            </ol>
          </Section>
        </div>

        {/* right: governance + executive + knowledge + outcome */}
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-black/20 p-3.5">
            <Section label="Governance" icon={<Landmark className="h-3 w-3 text-cyan-300" />}>
              <div className="space-y-2 text-[12px] text-white/70">
                <div><span className="text-white/40">Authority: </span>{g.authority}</div>
                <div className="flex flex-wrap gap-1.5">
                  {(g.policies || []).length ? (g.policies || []).map((p) => (
                    <span key={p} className="rounded-md border border-indigo-400/25 bg-indigo-400/10 px-2 py-0.5 font-mono text-[10px] text-indigo-200">{p}</span>
                  )) : <span className="text-white/40">No blocking policies</span>}
                </div>
                {g.traceability && <p className="text-[11px] leading-snug text-white/45">{g.traceability}</p>}
              </div>
            </Section>
          </div>

          <div className="rounded-xl border border-pink-400/20 bg-pink-500/[0.05] p-3.5">
            <Section label="Executive Signal" icon={<Presentation className="h-3 w-3 text-pink-300" />}>
              <p className="text-[12px] leading-relaxed text-white/75">{d.executive_brief}</p>
            </Section>
          </div>

          <div className="rounded-xl border border-teal-400/20 bg-teal-500/[0.05] p-3.5">
            <Section label="Knowledge Learned" icon={<GraduationCap className="h-3 w-3 text-teal-300" />}>
              <p className="text-[12px] leading-relaxed text-white/75">{d.knowledge}</p>
              {(d.precedent_used || []).length > 0 && (
                <p className="mt-1.5 text-[11px] text-white/40">Precedent: {(d.precedent_used || []).join("; ")}</p>
              )}
            </Section>
          </div>

          {/* outcome — every action teaches the platform */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">Record Outcome</div>
            {decided ? (
              <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: status === "approved" ? "#34d399" : status === "rejected" ? "#ef4444" : "#f59e0b" }}>
                <Check className="h-4 w-4" /> {status[0].toUpperCase() + status.slice(1)} — written to enterprise memory
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button onClick={() => act("approved")} disabled={!!busy} className="flex items-center gap-1.5 rounded-lg bg-cyan-500 px-3 py-1.5 text-[12px] font-semibold text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="decision-object-approve">
                  {busy === "approved" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CircleCheck className="h-3.5 w-3.5" />} Approve
                </button>
                <button onClick={() => act("deferred")} disabled={!!busy} className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[12px] text-white/80 hover:border-amber-400/40 disabled:opacity-50" data-testid="decision-object-defer">
                  {busy === "deferred" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock className="h-3.5 w-3.5 text-amber-300" />} Defer
                </button>
                <button onClick={() => act("rejected")} disabled={!!busy} className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[12px] text-white/80 hover:border-red-400/40 disabled:opacity-50" data-testid="decision-object-reject">
                  {busy === "rejected" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CircleSlash className="h-3.5 w-3.5 text-red-300" />} Reject
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ============ Main screen ============ */
export function CognitiveCore() {
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [running, setRunning] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [memory, setMemory] = useState({ memory: [], count: 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    nexsysApi.cognitive().then(setMeta).catch(() => setMeta(null));
    loadMemory();
    // allow Chatty to push a governed Decision Object onto this screen
    window.__nexsysShowDecision = (d) => { setResult(d); loadMemory(); };
    if (window.__nexsysPendingDecision) { setResult(window.__nexsysPendingDecision); window.__nexsysPendingDecision = null; }
    return () => { clearInterval(timerRef.current); delete window.__nexsysShowDecision; };
  }, []);
  const loadMemory = () => nexsysApi.memory(10).then(setMemory).catch(() => {});

  const orchestrate = async (question) => {
    const text = (question ?? q).trim();
    if (!text || running) return;
    setResult(null);
    setRunning(true);
    setActiveIdx(0);
    const n = meta?.agents?.length || 10;
    timerRef.current = setInterval(() => setActiveIdx((i) => Math.min(i + 1, n - 1)), 800);
    try {
      const d = await nexsysApi.orchestrate({ question: text, view: window.__nexsysView || "cognitive" });
      setResult(d);
      loadMemory();
    } catch {
      setResult(null);
    } finally {
      clearInterval(timerRef.current);
      setRunning(false);
      setActiveIdx(n);
    }
  };

  const agentsMeta = meta?.agents || [];

  return (
    <div className="space-y-5" data-testid="cognitive-core">
      <Panel
        title="Cognitive Core — Enterprise Cognitive Substrate"
        subtitle="NEXSYS · COMPUTATIONAL INTELLIGENCE PLATFORM · AWARENESS → SEMANTICS → STATE → DECISION → GOVERNANCE → KNOWLEDGE"
        right={<Pill tone="cyan"><BrainCircuit className="h-3 w-3" /> {agentsMeta.length} agents</Pill>}
      >
        <p className="text-[13px] leading-relaxed text-white/55">
          NEXSYS is the cognitive substrate beneath EDIOS. Pose any enterprise decision and specialized
          computational agents reason independently over one governed enterprise state. NEXSYS orchestrates
          their reasoning into an <span className="text-cyan-200">EDIOS Decision Object</span> carrying full
          explainability — why, how, evidence, policy, confidence, alternatives — together with persistent
          enterprise memory.
        </p>
      </Panel>

      {/* SCIOUS → NEXSYS → EDIOS */}
      {meta?.stack && (
        <Panel title="Positioning" subtitle="COMPUTATIONAL AWARENESS PARADIGM → COMPUTATIONAL INTELLIGENCE PLATFORM → DECISION INTELLIGENCE OS">
          <StackVisual stack={meta.stack} />
        </Panel>
      )}

      {/* Orchestration console */}
      <Panel title="Orchestration Console" subtitle="POSE A DECISION · AGENTS REASON · GOVERNED DECISION OBJECT" right={<Pill tone={running ? "amber" : "cyan"}>{running ? "orchestrating…" : "ready"}</Pill>}>
        <form onSubmit={(e) => { e.preventDefault(); orchestrate(); }} className="flex items-center gap-2">
          <input
            value={q} onChange={(e) => setQ(e.target.value)} disabled={running}
            placeholder="e.g. Should we accelerate ERP integration testing?"
            className="flex-1 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50 disabled:opacity-60"
            data-testid="orchestrate-input"
          />
          <button type="submit" disabled={running || !q.trim()} className="flex h-10 items-center gap-1.5 rounded-xl bg-cyan-500 px-4 text-sm font-semibold text-black transition-opacity disabled:opacity-50" data-testid="orchestrate-submit">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Orchestrate
          </button>
        </form>
        {!running && !result && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => { setQ(s); orchestrate(s); }} className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-white/55 hover:border-cyan-400/40 hover:text-white" data-testid="orchestrate-suggestion">{s}</button>
            ))}
          </div>
        )}

        {(running || result) && agentsMeta.length > 0 && (
          <div className="mt-4">
            <AgentPipeline agents={agentsMeta} running={running} activeIdx={activeIdx} result={result} />
          </div>
        )}
      </Panel>

      {result && <DecisionObject d={result} onOutcome={loadMemory} />}

      {/* Cognitive services */}
      {meta?.services && (
        <Panel title="Enterprise Cognitive Services" subtitle="THE PERMANENT COMPUTATIONAL CAPABILITIES">
          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3" data-testid="cognitive-services">
            {meta.services.map((s, i) => (
              <motion.div key={s.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-400/10 text-cyan-300"><Icon name={s.icon} className="h-4 w-4" /></span>
                  <div className="font-display text-[13px] font-semibold text-white/90">{s.name}</div>
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-wider text-cyan-300/70">{s.question}</div>
                <p className="mt-1 text-[11px] leading-snug text-white/45">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </Panel>
      )}

      {/* Invariants */}
      {meta?.invariants && (
        <Panel title="Platform Invariants" subtitle="THE RULES EVERY AGENT MUST RESPECT">
          <div className="grid gap-2 sm:grid-cols-2" data-testid="invariants">
            {meta.invariants.map((inv) => (
              <div key={inv.n} className="flex items-start gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-400/15 font-mono text-[11px] font-bold text-cyan-300">{inv.n}</span>
                <div>
                  <div className="text-[12px] font-semibold text-white/85">{inv.title}</div>
                  <div className="text-[11px] leading-snug text-white/45">{inv.rule}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Enterprise memory */}
      <Panel title="Enterprise Memory" subtitle="EVERY DECISION PERSISTS · HISTORY · OUTCOMES · PRECEDENT" right={<Pill tone="indigo"><Database className="h-3 w-3" /> {memory.count} decisions</Pill>}>
        {memory.memory.length === 0 ? (
          <p className="py-4 text-[13px] text-white/45">No decisions yet. Pose a decision above — it will be reasoned, governed, and written to enterprise memory.</p>
        ) : (
          <div className="space-y-2" data-testid="memory-feed">
            {memory.memory.map((m) => {
              const stColor = { approved: "#34d399", rejected: "#ef4444", deferred: "#f59e0b", proposed: "#94a3b8" }[m.status] || "#94a3b8";
              return (
                <div key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="memory-row">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: stColor, boxShadow: `0 0 8px ${stColor}` }} />
                  <span className="font-display text-[13px] font-semibold text-white/90">{m.title}</span>
                  <span className="min-w-0 flex-1 truncate text-[12px] text-white/45">{m.summary}</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: stColor }}>{m.status}</span>
                  <span className="font-mono text-[10px] text-white/30">{(m.created_at || "").slice(0, 10)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}
